import React, { useEffect, useState } from 'react';
import {
  Box, HStack, Input, InputGroup, InputLeftElement, Text, useColorModeValue,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { useQuery } from 'react-query';
import axios from 'axios';
import DashPanel from './DashPanel';

// item 351 — the everything log (admin-only): every mutation, by user, live.
// item 1044 (Chad: "audit log is only visible when the live dash is expanded — add it to
// the universe dash") — extracted into a shared card so it lives on BOTH the universe
// dashboard (a reorderable card) and the live dashboard (same component, same feed).
export interface AuditRow {
  id: string;
  timestamp: string;
  source: string;
  entityType: string;
  action: string;
  summary?: string | null;
  actorName?: string | null;
}

// item 1045 (Chad) — search is SERVER-side (the feed only holds the newest rows; a client
// filter would miss history). The query key carries the term so results cache per search.
export const useAuditFeed = (enabled: boolean, q = '') => useQuery<AuditRow[]>(
  ['auditFeed', q],
  async () => {
    const apiUrl = process.env.REACT_APP_BASIX_API || '';
    const { data } = await axios.get(`${apiUrl}Audit`, { params: { take: q ? 200 : 100, q: q || undefined } });
    return Array.isArray(data) ? data : [];
  },
  { enabled, refetchInterval: 10_000, keepPreviousData: true },
);

const AuditLogCard: React.FC = () => {
  // item 1045 — debounced search box: type freely, the query fires 300ms after you stop
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);
  const { data: audit } = useAuditFeed(true, term);
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  return (
    <DashPanel label="Audit log — everything, live" accent="brand.300">
      <Text fontSize="xs" color={subColor} mb={2}>
        Every change, by user, as it happens — admin-only, newest first, refreshes every 10s.
      </Text>
      <InputGroup size="sm" mb={2}>
        <InputLeftElement pointerEvents="none"><FiSearch opacity={0.5} /></InputLeftElement>
        <Input
          placeholder="Search the whole log — user, entity, action, details…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          borderRadius="md"
        />
      </InputGroup>
      <Box maxH="420px" overflowY="auto" borderWidth="1px" borderRadius="lg" p={2}>
        {(audit ?? []).length === 0 ? (
          <Text fontSize="sm" opacity={0.6} p={3}>
            {term ? `Nothing in the log matches “${term}”.` : 'No events yet — every change lands here as it happens.'}
          </Text>
        ) : (audit ?? []).map((e) => (
          <HStack key={e.id} py={1} px={2} spacing={3} borderBottomWidth="1px" align="baseline">
            <Text fontSize="xs" opacity={0.55} minW="72px">{new Date(e.timestamp).toLocaleTimeString()}</Text>
            <Text fontSize="xs" fontWeight="semibold" minW="110px" noOfLines={1}>{e.actorName ?? e.source}</Text>
            <Text fontSize="xs" opacity={0.8} minW="150px" noOfLines={1}>{`${e.action} ${e.entityType}`}</Text>
            <Text fontSize="xs" opacity={0.65} noOfLines={1}>{e.summary ?? ''}</Text>
          </HStack>
        ))}
      </Box>
    </DashPanel>
  );
};

export default AuditLogCard;
